import React from 'react';
import { Invoice } from '../types';

interface YearlyIncomeChartProps {
  invoices: Invoice[];
}

export const YearlyIncomeChart: React.FC<YearlyIncomeChartProps> = ({ invoices }) => {
  // Calcular ingresos por mes del año actual
  const currentYear = new Date().getFullYear();
  const monthlyData = Array.from({ length: 12 }, (_, index) => {
    const monthInvoices = invoices.filter(inv => {
      const invoiceDate = new Date(inv.date);
      return invoiceDate.getMonth() === index && 
             invoiceDate.getFullYear() === currentYear &&
             inv.status === 'paid'; // Solo facturas pagadas
    });
    
    return monthInvoices.reduce((sum, inv) => sum + inv.total, 0);
  });

  const maxValue = Math.max(...monthlyData, 1); // Evitar división por 0
  const totalYearlyIncome = monthlyData.reduce((sum, amount) => sum + amount, 0);
  
  // Crear IDs únicos para evitar conflictos
  const chartId = Math.random().toString(36).substr(2, 9);
  const gridId = `grid-${chartId}`;
  const gradientId = `areaGradient-${chartId}`;
  
  // Calcular puntos para el gráfico SVG
  const width = 200;
  const height = 60;
  const padding = 10;
  
  const points = monthlyData.map((value, index) => {
    const x = padding + (index * (width - 2 * padding)) / 11;
    const y = height - padding - ((value / maxValue) * (height - 2 * padding));
    return `${x},${y}`;
  }).join(' ');

  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const currentMonth = new Date().getMonth();

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Evolución Anual {currentYear}
        </h3>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600">
            {totalYearlyIncome.toFixed(2)} €
          </div>
          <div className="text-sm text-gray-500">Total del año</div>
        </div>
      </div>
      
      <div className="relative">
        <svg width={width} height={height} className="w-full h-16">
          {/* Grid lines and gradients */}
          <defs>
            <pattern id={gridId} width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f3f4f6" strokeWidth="1"/>
            </pattern>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3"/>
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.1"/>
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill={`url(#${gridId})`} />
          
          {/* Area bajo la curva */}
          <path
            d={`M ${padding},${height - padding} L ${points} L ${width - padding},${height - padding} Z`}
            fill={`url(#${gradientId})`}
          />
          
          {/* Línea principal */}
          <polyline
            fill="none"
            stroke="#3b82f6"
            strokeWidth="2"
            points={points}
          />
          
          {/* Puntos en cada mes */}
          {monthlyData.map((value, index) => {
            const x = padding + (index * (width - 2 * padding)) / 11;
            const y = height - padding - ((value / maxValue) * (height - 2 * padding));
            const isCurrentMonth = index === currentMonth;
            
            return (
              <g key={index}>
                <circle
                  cx={x}
                  cy={y}
                  r={isCurrentMonth ? "4" : "3"}
                  fill={isCurrentMonth ? "#ef4444" : "#3b82f6"}
                  stroke="white"
                  strokeWidth="1"
                />
                {/* Tooltip en hover */}
                <title>
                  {monthNames[index]}: {value.toFixed(2)} €
                </title>
              </g>
            );
          })}
        </svg>
        
        {/* Etiquetas de meses */}
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          {monthNames.map((month, index) => (
            <span key={month} className={`${index === currentMonth ? 'font-bold text-red-600' : ''}`}>
              {month}
            </span>
          ))}
        </div>
      </div>
      
      {/* Estadísticas adicionales */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-sm font-medium text-gray-900">
            {monthlyData[currentMonth].toFixed(2)} €
          </div>
          <div className="text-xs text-gray-500">Este mes</div>
        </div>
        <div>
          <div className="text-sm font-medium text-gray-900">
            {(totalYearlyIncome / (currentMonth + 1)).toFixed(2)} €
          </div>
          <div className="text-xs text-gray-500">Media mensual</div>
        </div>
        <div>
          <div className="text-sm font-medium text-gray-900">
            {Math.max(...monthlyData).toFixed(2)} €
          </div>
          <div className="text-xs text-gray-500">Mes máximo</div>
        </div>
      </div>
    </div>
  );
}; 